import { http, HttpResponse } from 'msw';

/**
 * SidebarCounts — numeric badges displayed next to the sidebar entries
 * for Replays, Audit packages and Attestations, plus the Divergence map.
 *
 * The values used to be hard-coded (`14`, `247`, `●1`) — see the
 * `docs/review/ui-middleware-audit.md` finding #5. They are now fetched
 * from `/api/v1/sidebar-counts` so they reflect actual system state.
 */
export interface SidebarCounts {
  scenarios: number;
  replays: number;
  alerts: number;
  divergences: number;
  auditPackages: number;
  attestationsPending: number;
}

export const SIDEBAR_COUNTS_ENDPOINT = '/api/v1/sidebar-counts';

/** Deterministic fixture used by MSW handlers and the in-memory client. */
export const MOCK_SIDEBAR_COUNTS: SidebarCounts = {
  scenarios: 1_247,
  replays: 14,
  alerts: 142,
  divergences: 7,
  auditPackages: 247,
  attestationsPending: 1,
};

/**
 * In-memory fallback used by the dev server and by the component default.
 * Mirrors the `createMockXApi()` pattern every screen uses: dev/test never
 * touches the network unless explicitly wired to. The real client will hit
 * `GET /api/v1/sidebar-counts` via the fetcher below once `sentinel-api`
 * ships.
 */
export async function fetchSidebarCountsMock(): Promise<SidebarCounts> {
  return MOCK_SIDEBAR_COUNTS;
}

/** Real network fetcher — reserved for the eventual HTTP client. */
export async function fetchSidebarCountsHttp(): Promise<SidebarCounts> {
  const res = await fetch(SIDEBAR_COUNTS_ENDPOINT, {
    headers: { accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch sidebar counts: ${res.status}`);
  }
  return (await res.json()) as SidebarCounts;
}

/**
 * Default fetcher injected by `Layout`. Using the in-memory mock mirrors
 * every screen's `DEFAULT_API = createMockXApi()` convention.
 */
export const fetchSidebarCounts = fetchSidebarCountsMock;

export const sidebarCountsHandlers = [
  http.get(SIDEBAR_COUNTS_ENDPOINT, () => HttpResponse.json(MOCK_SIDEBAR_COUNTS)),
];
